import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { HOST_API } from '../config';
import { captureAuthSessionCleared, captureAuthRefreshStage } from './authSessionTelemetry';

let refreshPromise = null;
// Per-tab single-flight in front of the Web Lock. Without it, every request
// that finds the access token near expiry queues its OWN lock request: the
// first refreshes and the rest each wake up to a token another caller in this
// same tab already rotated (JAVASCRIPT-REACT-15B, ~260 "session cleared"
// warnings a week, none of them a logout). Kept separate from refreshPromise
// because runWithLock's lock-timeout escape hatch uses that one.
let lockedRefreshPromise = null;
let lockUnavailableWarned = false;
const REFRESH_TIMEOUT_MS = 15000;
const REFRESH_LOCK_NAME = 'crm-auth-refresh';
// Bounds how long a call will wait to ACQUIRE the Web Lock before giving up
// and refreshing directly. Without this, a hung sibling tab holding the lock
// (or an N-tab pile-up all queued behind it) can block every other tab's
// refresh indefinitely.
const REFRESH_LOCK_WAIT_MS = 15000;

// Guarded inline rather than via localStorageGetItem: that helper probes
// availability with a setItem+removeItem on every call, and these sit on the
// per-request refresh path. Same guard pattern as hooks/useDemoMode.js.
function getRefreshToken() {
    try { return localStorage.getItem('refreshToken'); } catch { return null; }
}

function getAccessToken() {
    try { return localStorage.getItem('accessToken'); } catch { return null; }
}

function hasWebLocks() {
    return typeof navigator !== 'undefined' && Boolean(navigator.locks && navigator.locks.request);
}

// Extracts the user identity claim the same way authSessionTelemetry does,
// so "same user" comparisons are consistent across the codebase. Exported so
// jwt.js's storage listener can apply the exact same check before installing
// a peer-written token, instead of duplicating the decode logic.
export function decodeUserIdentity(token) {
    if (!token) {
        return { ok: false, userId: null };
    }
    try {
        const decoded = jwtDecode(token);
        const userId = decoded?.user_id || decoded?.userId || decoded?.sub || null;
        return { ok: true, userId };
    } catch {
        return { ok: false, userId: null };
    }
}

function isExpiredJwt(token) {
    if (!token) return false;
    try {
        const { exp } = jwtDecode(token);
        return Number(exp) <= Date.now() / 1000;
    } catch {
        return false;
    }
}

// Default skew for the pre-request check in callApi: a token that expires
// within this window is refreshed BEFORE the request goes out, instead of the
// request being sent, rejected with 401, refreshed and retried. Wide enough to
// absorb clock drift and request latency; narrow enough that a 60-minute
// token is still used for ~59 minutes.
export const ACCESS_TOKEN_REFRESH_SKEW_SECONDS = 45;

/**
 * True when ``token`` is a decodable JWT that has expired or will expire
 * within ``skewSeconds``. An undecodable token returns false: that is not
 * this function's call, the request goes out and the 401 path decides.
 */
export function accessTokenNeedsRefresh(token, skewSeconds = ACCESS_TOKEN_REFRESH_SKEW_SECONDS) {
    if (!token) return false;
    try {
        const { exp } = jwtDecode(token);
        return Number(exp) <= Date.now() / 1000 + skewSeconds;
    } catch {
        return false;
    }
}

function classifyRefreshFailure(error, refreshToken) {
    const status = error?.response?.status || 0;

    if (status === 401 || status === 403) {
        return isExpiredJwt(refreshToken) ? 'refresh_expired' : 'refresh_invalid';
    }
    if (status >= 500) {
        return 'refresh_server_error';
    }
    if (!status || error?.code === 'ECONNABORTED' || error?.message === 'Network Error') {
        return 'refresh_network_error';
    }
    return 'refresh_network_error';
}

function shouldClearSession(reason) {
    return reason === 'refresh_invalid'
        || reason === 'refresh_expired'
        || reason === 'refresh_user_changed'
        || reason === 'refresh_rotation_adopt_failed';
}

export class AuthRefreshError extends Error {
    constructor(reason, error) {
        super(reason);
        this.name = 'AuthRefreshError';
        this.reason = reason;
        this.status = error?.response?.status || 0;
        this.code = error?.code;
        this.originalError = error;
        this.clearSession = shouldClearSession(reason);
        this.response = error?.response;
    }
}

async function clearInvalidSession(reason, error) {
    const { setSession } = await import('./jwt');
    setSession(null, null, {
        reason,
        status: error?.response?.status || 0,
        error_code: error?.code,
    });
}

// The stored refresh token no longer matches the one this call started with —
// a sibling tab (or the backend, with rotation enabled) has already rotated
// it, removed it (logout), or a different user has logged in on this tab.
// Decide whether to adopt the peer's session or clear ours; never write our
// own (now-stale) response over whatever is currently stored.
async function resolvePeerRotation(originalRefreshToken) {
    const currentRefreshToken = getRefreshToken();

    if (!currentRefreshToken) {
        // Peer logout (or an already-cleared session) mid-flight. Must clear.
        const error = new AuthRefreshError('refresh_invalid', { response: { status: 401 } });
        await clearInvalidSession(error.reason, { response: { status: 401 } });
        throw error;
    }

    const original = decodeUserIdentity(originalRefreshToken);
    const current = decodeUserIdentity(currentRefreshToken);

    if (!original.ok || !current.ok || !original.userId || !current.userId) {
        // Either token failed to decode (or carries no identity claim) — we
        // cannot safely verify this is the same user. Must clear.
        const error = new AuthRefreshError('refresh_rotation_adopt_failed', { response: { status: 401 } });
        await clearInvalidSession(error.reason, { response: { status: 401 } });
        throw error;
    }

    if (original.userId !== current.userId) {
        // A different user is now logged in on this tab. Never adopt.
        const error = new AuthRefreshError('refresh_user_changed', { response: { status: 401 } });
        await clearInvalidSession(error.reason, { response: { status: 401 } });
        throw error;
    }

    // Same user: adopt the peer's already-rotated session instead of
    // clobbering it, and hand back the currently stored access token.
    captureAuthSessionCleared('refresh_token_rotated_by_peer', {
        session_preserved: true,
        peer_rotation: true,
    });
    return getAccessToken();
}

async function applyRefreshedSession(newAccess, nextRefreshToken, originalRefreshToken) {
    if (getRefreshToken() !== originalRefreshToken) {
        return resolvePeerRotation(originalRefreshToken);
    }
    const { setSession } = await import('./jwt');
    setSession(newAccess, nextRefreshToken);
    return newAccess;
}

async function doRefresh() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        const error = new AuthRefreshError('refresh_invalid', { response: { status: 401 } });
        await clearInvalidSession(error.reason, error);
        throw error;
    }

    try {
        const response = await axios.post(`${HOST_API}/api/v1/auth/token/refresh/`, {
            refresh: refreshToken,
        }, { timeout: REFRESH_TIMEOUT_MS });
        const newAccess = response?.data?.access;
        const newRefresh = response?.data?.refresh || refreshToken;
        if (response.status === 200 && newAccess) {
            return applyRefreshedSession(newAccess, newRefresh, refreshToken);
        }
        throw new AuthRefreshError('refresh_invalid', { response });
    } catch (error) {
        if (error instanceof AuthRefreshError) {
            throw error;
        }
        const reason = classifyRefreshFailure(error, refreshToken);
        if (shouldClearSession(reason) && getRefreshToken() !== refreshToken) {
            // The server rejected OUR (now possibly stale) refresh token, but
            // the stored one changed while the request was in flight — a
            // sibling tab may have already rotated it. Re-validate identity
            // instead of assuming genuine invalidation (e.g. password change,
            // which leaves the stored token unchanged and must still clear).
            return resolvePeerRotation(refreshToken);
        }
        const refreshError = new AuthRefreshError(reason, error);
        if (refreshError.clearSession) {
            await clearInvalidSession(reason, error);
        } else {
            captureAuthSessionCleared(reason, {
                status: error?.response?.status || 0,
                error_code: error?.code,
                session_preserved: true,
            });
        }
        throw refreshError;
    }
}

function hasAbortSignalTimeout() {
    return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
}

// Prefer the platform's AbortSignal.timeout when available; otherwise wire up
// an equivalent AbortController + setTimeout by hand.
function makeLockWaitSignal() {
    if (hasAbortSignalTimeout()) {
        return { signal: AbortSignal.timeout(REFRESH_LOCK_WAIT_MS), cleanup: () => {} };
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_LOCK_WAIT_MS);
    return { signal: controller.signal, cleanup: () => clearTimeout(timer) };
}

// The existing per-tab single-flight. Used directly when Web Locks are
// unavailable at all, and as the escape hatch when a lock acquisition times
// out (fix 3): a stuck/hung sibling tab must not block this tab's refresh
// forever.
function fallbackSingleFlight() {
    if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

async function runWithLock(startingRefreshToken) {
    const { signal, cleanup } = makeLockWaitSignal();
    try {
        return await navigator.locks.request(REFRESH_LOCK_NAME, { signal }, async () => {
            // First thing inside the lock: re-read storage. A sibling tab may
            // have already refreshed while we were waiting for the lock.
            const currentRefreshToken = getRefreshToken();
            if (currentRefreshToken === startingRefreshToken) {
                return doRefresh();
            }

            if (!currentRefreshToken) {
                // Peer logout while we were queued behind the lock. Clear.
                const error = new AuthRefreshError('refresh_invalid', { response: { status: 401 } });
                await clearInvalidSession(error.reason, { response: { status: 401 } });
                throw error;
            }

            // The stored refresh token changed while we waited — verify it's
            // still the SAME user before ever adopting whatever access token
            // is now stored. Exactly the same identity check as
            // resolvePeerRotation's adopt/clear decision; a different (or
            // undecodable) identity must clear, never be adopted.
            const starting = decodeUserIdentity(startingRefreshToken);
            const current = decodeUserIdentity(currentRefreshToken);
            const sameUser = starting.ok && current.ok && starting.userId && current.userId
                && starting.userId === current.userId;
            if (!sameUser) {
                const error = new AuthRefreshError('refresh_user_changed', { response: { status: 401 } });
                await clearInvalidSession(error.reason, { response: { status: 401 } });
                throw error;
            }

            const currentAccessToken = getAccessToken();
            const { isValidToken } = await import('./jwt');
            if (isValidToken(currentAccessToken)) {
                // Expected success path (a sibling tab refreshed first and we
                // adopt its token), so a breadcrumb, not a Sentry event: the
                // old "CRM auth session cleared" warning here was not a logout.
                captureAuthRefreshStage('refresh_lock_waited', {
                    session_preserved: true,
                });
                return currentAccessToken;
            }
            // Same user, but the stored access token isn't usable (e.g. also
            // stale/expired) — fall through to a real refresh.
            return doRefresh();
        });
    } catch (error) {
        if (error?.name === 'AbortError') {
            // The lock never became available within REFRESH_LOCK_WAIT_MS —
            // a hung sibling tab (or an N-tab pile-up) must not block this
            // tab's refresh indefinitely. Fall back to a direct, per-tab
            // refresh instead.
            captureAuthSessionCleared('refresh_lock_timeout', { session_preserved: true });
            return fallbackSingleFlight();
        }
        throw error;
    } finally {
        cleanup();
    }
}

export function refreshAccessToken() {
    if (hasWebLocks()) {
        if (!lockedRefreshPromise) {
            lockedRefreshPromise = runWithLock(getRefreshToken()).finally(() => {
                lockedRefreshPromise = null;
            });
        }
        return lockedRefreshPromise;
    }

    if (!lockUnavailableWarned) {
        lockUnavailableWarned = true;
        captureAuthSessionCleared('refresh_lock_unavailable', {
            session_preserved: true,
        });
    }

    return fallbackSingleFlight();
}

export const __authRefreshTestUtils = {
    classifyRefreshFailure,
    shouldClearSession,
    isExpiredJwt,
    resetLockUnavailableWarning: () => {
        lockUnavailableWarned = false;
    },
};
